import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import styles from "./InfoBlock.module.scss";
import { memo } from "react";
import useTranslationHook from "hooks/useTranslationHook";
import useIsLanguage from "hooks/useIsLanguage";
import wrapperHOC from "@Utils/wrapperHOC";
import config from "@Config/index";
import { renderChinaNamedDate } from "@Utils/cnDisplayDate";
import { chinaFormattedDate } from "@Pages/PropertyPage/components/RoomTypeSection/utils";
import isPastOrToday, { getTranslatedDurationValue } from "../../utils";

type InfoBlockProps = {
  label: string;
  value: string;
  isBold?: boolean;
  disabled?: boolean;
  isDesktop?: boolean;
  testId?: string;
  hideTitle?: boolean;
  className?: string;
};

const InfoBlock = ({
  label,
  value,
  isBold = false,
  disabled = false,
  isDesktop = false,
  testId,
  hideTitle = false,
  className,
}: InfoBlockProps) => {
  const { t } = useTranslationHook();
  const language = useIsLanguage();

  const isMoveInOutRow =
    label === t("propertypage.components.tenancycard.moveInDate") ||
    label === t("propertypage.components.tenancycard.moveOutDate");

  if (label == t("propertypage.components.tenancycard.moveInDate")) {
    label = isPastOrToday(value)
      ? t("propertypage.components.tenancycard.availableFrom")
      : t("propertypage.components.tenancycard.moveInDate");
  }

  const renderedValue = (() => {
    if (config.IS_CHINA_APP && isMoveInOutRow) {
      return renderChinaNamedDate(value, value);
    }
    const formattedValue = isMoveInOutRow ? chinaFormattedDate(value) : value;
    const chinaFormattedValue = getTranslatedDurationValue(formattedValue, t);
    if (language !== "en") {
      return t(chinaFormattedValue || formattedValue);
    }
    return value;
  })();

  return (
    <div
      className={formatClassNames(
        styles.infoBlockContainer,
        isDesktop && styles.desktop,
        className,
      )}
    >
      {!hideTitle && (
        <span data-testid={`property-room-type-tenancy-${label}`} className={styles.label}>
          {t(label)}
          {isDesktop ? "" : ": "}
        </span>
      )}
      <span
        data-testid={testId}
        className={formatClassNames(
          styles.value,
          isBold && styles.isBold,
          disabled && styles.disabled,
          isDesktop && styles.desktop,
        )}
      >
        {renderedValue}
      </span>
      {hideTitle && disabled && (
        <span
          data-testid="property-room-type-tenancy-soldout"
          className={formatClassNames(styles.soldOutBadge, isDesktop && styles.desktop)}
        >
          {t("propertypage.roomtypesectiondynamic.soldout")}
        </span>
      )}
    </div>
  );
};

export default wrapperHOC(memo(InfoBlock), {
  componentName: "InfoBlock-TenancyCard-RoomTypeCard-RoomTypeSection-PropertyPage",
  showForChina: true,
});
